import React, { useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import CommunicationButtons from "./CommunicationButtons";
import UserActionsDropdown from "./UserActionsDropdown";
import configCountries from "../../../Config/countries.json";

const UserCard = ({ item, t, onAction }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!item) return null;

    const countryObj = item.Country
        ? configCountries.countries.find(
              (c) => c.value.toLowerCase() === item.Country.toLowerCase()
          )
        : null;

    const userRole = item.UserType ? item.UserType.Name : "";
    const userTypeId = item.UserType?.Id;
    const isTeacher = userTypeId === 2 || userTypeId === 4;
    const isStudent = userTypeId === 5;
    const isAdmin = userTypeId === 1;

    let roleBadgeClass = "badge-secondary";
    if (isAdmin) roleBadgeClass = "badge-danger";
    else if (isTeacher) roleBadgeClass = "badge-info";
    else if (isStudent) roleBadgeClass = "badge-primary";

    // Status dot
    let statusDotClass = "status-dot--inactive";
    let statusTitle = t("Pages.UsersControl.NotActive");
    if (item.IsLocked) {
        statusDotClass = "status-dot--locked";
        statusTitle = t("Pages.UsersControl.Locked");
    } else if (item.IsActive) {
        statusDotClass = "status-dot--active";
        statusTitle = t("Pages.UsersControl.Active");
    }

    return (
        <div className="user-card" style={{ paddingBottom: '12px' }}>
            {/* Status Dot */}
            <div className="user-card-status-dot">
                <span className={`status-dot ${statusDotClass}`} title={statusTitle}></span>
            </div>

            {/* Header: Avatar + Name */}
            <div className="user-card-header">
                <div 
                    className="user-avatar-wrapper position-relative"
                    title={item.ActivateUntilDate && item.ActivateUntilDate !== "0" ? `${t("Pages.Profile.ActivateUntilDate.title")}: ${item.ActivateUntilDate}` : undefined}
                    style={{ width: '48px', height: '48px', minWidth: '48px', margin: 0 }}
                >
                    <LazyLoadImage
                        src={item.Photo || "/assets/images/logo/130p150.png"}
                        alt={item.Name}
                        className={`user-card-avatar user-avatar-img ${item.IsActive ? "border-active" : "border-inactive"}`}
                        title={item.IsActive ? t("Pages.UsersControl.Active") : t("Pages.UsersControl.NotActive")}
                        style={{ width: '100%', height: '100%' }}
                    />
                    {item.ActivateUntilDate && item.ActivateUntilDate !== "0" && (
                        <span className="user-crown-badge">
                            <i className="fa-solid fa-crown"></i>
                        </span>
                    )}
                    {!item.IsActive && (
                        <span
                            className="position-absolute d-flex align-items-center justify-content-center bg-danger text-white rounded-circle shadow-sm user-avatar-badge-top"
                            title={t("Pages.UsersControl.NotActive")}
                            style={{ width: '18px', height: '18px', fontSize: '0.65rem' }}
                        >
                            <i className="fas fa-lock"></i>
                        </span>
                    )}
                    {item.IsLocked && (
                        <span
                            className="position-absolute d-flex align-items-center justify-content-center bg-dark text-white rounded-circle shadow-sm user-avatar-badge-bottom"
                            title={t("Pages.UsersControl.IsLocked")}
                            style={{ width: '18px', height: '18px', fontSize: '0.65rem' }}
                        >
                            <i className="fas fa-user-lock"></i>
                        </span>
                    )}
                </div>
                <div>
                    <div className="d-flex align-items-center">
                        <div
                            className="user-card-name"
                            onClick={() => onAction(item.IsDeleted ? "restore" : "edit", item)}
                        >
                            {item.Name}
                        </div>
                        {item.Sex && (
                            <i 
                                className={`fas fa-${item.Sex === "female" ? "venus text-danger" : "mars text-primary"} mr-2`} 
                                title={t('Pages.Register.Sex.' + item.Sex)}
                                style={{ fontSize: '0.85rem' }}
                            ></i>
                        )}
                    </div>
                    <div className="user-card-username">@{item.Username}</div>
                </div>
                <div className="mr-auto">
                    <span className={`badge ${roleBadgeClass} user-role-badge`}>
                        {userRole}
                    </span>
                </div>
            </div>

            {/* Details Grid */}
            <div className="user-card-details">
                {item.Email && (
                    <div className="user-card-detail-item">
                        <span className="user-card-detail-label">
                            <i className="fal fa-envelope ml-1"></i>
                            {t("Pages.UsersControl.tableHeaders.contact")}
                        </span>
                        <span className="user-card-detail-value" style={{ wordBreak: 'break-all' }}>{item.Email}</span>
                    </div>
                )}
                {item.Phone && (
                    <div className="user-card-detail-item">
                        <span className="user-card-detail-label">
                            <i className="fal fa-phone-alt ml-1"></i>
                            {t("Pages.UsersControl.advancedSearch.phone")}
                        </span>
                        <span className="user-card-detail-value" dir="ltr">{item.Phone}</span>
                    </div>
                )}
                {countryObj && (
                    <div className="user-card-detail-item">
                        <span className="user-card-detail-label">
                            {t("Pages.UsersControl.tableHeaders.country")}
                        </span>
                        <span className="user-card-detail-value d-flex align-items-center gap-1">
                            <LazyLoadImage
                                src={process.env.PUBLIC_URL + countryObj.icon}
                                alt={countryObj.label}
                                className="user-country-flag"
                                style={{ width: '24px', height: '16px' }}
                                title={`${countryObj.label}${item.Address ? ' - ' + item.Address : ''}`}
                            />
                            {countryObj.label}
                        </span>
                    </div>
                )}
                {item.School && userTypeId !== 1 && userTypeId !== 6 && (
                    <div className="user-card-detail-item">
                        <span className="user-card-detail-label">
                            <i className="fal fa-school ml-1"></i>
                            {t("Pages.UsersControl.tableHeaders.school")}
                        </span>
                        <span className="user-card-detail-value">{item.School.Name}</span>
                    </div>
                )}
            </div>

            {/* Collapsible Expanded Details Section */}
            {isExpanded && (
                <div className="user-card-expanded-details mt-3 pt-3 border-top" style={{ borderTop: '1px dashed var(--border-light)' }}>
                    <div className="user-card-details">
                        {/* Class Room (Student) */}
                        {item.UserType?.Id === 5 && item.ClassRoom && (
                            <div className="user-card-detail-item">
                                <span className="user-card-detail-label">
                                    <i className="fal fa-chalkboard-teacher ml-1 text-primary"></i>
                                    {t("Pages.Profile.ClassRoom.title")}
                                </span>
                                <span className="user-card-detail-value">{item.ClassRoom}</span>
                            </div>
                        )}
                        {/* Activation Code */}
                        {item.ActivateCode && (
                            <div className="user-card-detail-item">
                                <span className="user-card-detail-label">
                                    <i className="fal fa-key ml-1 text-warning"></i>
                                    {t("Pages.Profile.ActivateCode.title")}
                                </span>
                                <span className="user-card-detail-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                    {item.ActivateCode}
                                </span>
                            </div>
                        )}
                        {/* Birthday */}
                        {item.Birthday && (
                            <div className="user-card-detail-item">
                                <span className="user-card-detail-label">
                                    <i className="fal fa-birthday-cake ml-1 text-danger"></i>
                                    {t("Pages.Register.Birthday.title")}
                                </span>
                                <span className="user-card-detail-value">{item.Birthday}</span>
                            </div>
                        )}
                        {/* Address */}
                        {item.Address && (
                            <div className="user-card-detail-item">
                                <span className="user-card-detail-label">
                                    <i className="fal fa-map-marker-alt ml-1 text-warning"></i>
                                    {t("Pages.Register.Address.title") || "العنوان"}
                                </span>
                                <span className="user-card-detail-value">{item.Address}</span>
                            </div>
                        )}
                        {/* Last Login */}
                        {item.LastLogin && (
                            <div className="user-card-detail-item">
                                <span className="user-card-detail-label">
                                    <i className="fal fa-history ml-1 text-info"></i>
                                    {t("Pages.Profile.LastLogin.title")}
                                </span>
                                <span className="user-card-detail-value">{item.LastLogin}</span>
                            </div>
                        )}
                        {/* Creation Date */}
                        {item.CreationDate && (
                            <div className="user-card-detail-item col-span-2" style={{ gridColumn: 'span 2' }}>
                                <span className="user-card-detail-label">
                                    <i className="fal fa-calendar-plus ml-1 text-secondary"></i>
                                    {t("Pages.Lessons.CreationDate")}
                                </span>
                                <span className="user-card-detail-value">{item.CreationDate}</span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Footer: Communication + Actions */}
            <div className="user-card-footer mt-3 pt-3" style={{ borderTop: '1px solid #f1f5f9' }}>
                <CommunicationButtons item={item} t={t} />
                <div className="d-flex align-items-center gap-2">
                    <button
                        type="button"
                        className="btn btn-sm d-flex align-items-center justify-content-center"
                        onClick={() => setIsExpanded(!isExpanded)}
                        title={isExpanded ? t("Pages.UsersControl.showLess") : t("Pages.UsersControl.showMore")}
                        style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            border: '1.5px solid var(--border-medium)',
                            background: 'var(--surface-primary)',
                            color: 'var(--text-secondary)',
                            padding: 0,
                            cursor: 'pointer'
                        }}
                    >
                        <i className={`far fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ fontSize: '0.8rem' }}></i>
                    </button>
                    <UserActionsDropdown item={item} t={t} onAction={onAction} />
                </div>
            </div>
        </div>
    );
};

export default UserCard;
